import { type FC } from "react";

import { clsx } from "clsx";

import { CasesRail } from "./cases_rail";
import { DetailRail } from "./detail_rail";
import { NavGear } from "./nav_gear";
import { useNavRail } from "@application/contexts/nav_rail_context";

// The single persistent dashboard rail. One element spans the list↔case-detail
// route change so the nav *folds* between levels instead of hard-swapping: the
// shell width animates 192px (list) ↔ 56px (case detail), and the two content
// sets crossfade as stacked layers — the list status/builder nav fades out as
// the rail narrows, the thin Home rail fades in ~100ms behind. The settings gear
// is the fixed anchor the rail folds around: rendered once, pinned bottom-left,
// never moving. Fixed inner widths + overflow-clip keep each layer from
// reflowing while the shell animates. prefers-reduced-motion → instant.
//
// `open` is the mobile off-canvas drawer state. Below md the rail is always the
// wide drawer (the thin-rail morph is desktop-only), opened by ≡ at every level;
// at md+ it sits in the flow and folds, the case-detail level sliding away via
// the banner ≡ (railHidden).
export const DashboardRail: FC<{ open: boolean }> = ({ open }) => {
  const rail = useNavRail();
  const caseDetail = rail?.caseDetail ?? false;
  const railHidden = rail?.railHidden ?? false;
  const isDesktop = rail?.isDesktop ?? true;
  // The thin Home rail only exists at md+ on a case page; below md every level
  // shows the wide CasesRail drawer, so `inert` follows the morph, not the level.
  const folded = caseDetail && isDesktop;

  return (
    <div
      className={clsx(
        "z-40 h-full shrink-0 overflow-hidden transition-[width,margin-left,opacity,transform] duration-300 ease-out motion-reduce:transition-none",
        // Below md the rail is `fixed` — out of flow, so its column collapses to
        // zero and the content goes full-bleed (no reserved gutter); it slides in
        // off-canvas as a drawer. md+ it's `relative`: back in the flow and the
        // positioning context for the absolute layers, folding to the thin
        // width on a case page. (Plain `relative` would beat `fixed` in the
        // cascade and pin the column open below md — the blank-gutter bug.)
        "rail fixed inset-y-0 left-0 w-48 md:relative md:inset-auto",
        open ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        caseDetail && "md:w-14",
        caseDetail && railHidden && "md:-ml-14 md:opacity-0",
      )}
    >
      {/* List-level content — the wide drawer below md, fading out as the rail
          narrows at md+. Both layers are always mounted, so the folded one is
          `inert`: out of the tab order and the a11y tree, not just visually gone.
          `pb-14` reserves room for the absolute gear so tall content can't slide
          under it on a short viewport. */}
      <div
        inert={folded}
        className={clsx(
          "absolute inset-0 flex w-48 flex-col px-3 pt-3.5 pb-14 opacity-100 transition-opacity duration-200 ease-out motion-reduce:transition-none",
          caseDetail && "md:pointer-events-none md:opacity-0",
        )}
      >
        <CasesRail />
      </div>

      {/* Case-detail thin rail — md+ only; fades in ~100ms behind the fold. */}
      <div
        inert={!folded}
        className={clsx(
          "absolute inset-0 hidden w-14 flex-col items-center px-2 pt-3 opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none md:flex",
          caseDetail
            ? "md:opacity-100 md:delay-100"
            : "md:pointer-events-none md:opacity-0",
        )}
      >
        <DetailRail />
      </div>

      {/* The gear is the stationary anchor — bottom-left, identical in both
          states. One constant tone (the bordered chip) so its size never
          changes as the rail folds, keeping the bottom-left corner fixed. */}
      <div className="absolute bottom-3 left-2">
        <NavGear />
      </div>
    </div>
  );
};
